import React, { useEffect } from 'react';
import SEO from '../../components/SEO';

const TermsPage = () => {
    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-white text-gray-800 font-sans">
            <SEO
                title="Terms of Service"
                description="Terms of Service and conditions for using gio billing software and services."
                url="/terms-of-service"
            />

            {/* Dark Theme Header Banner */}
            <div className="bg-[#0f172a] text-white py-12 px-6 md:px-12 relative overflow-hidden">
                <div className="max-w-4xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 relative z-10">
                    <div className="space-y-1">
                        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#10b981]">Terms of Service</h1>
                        <p className="text-sm text-gray-400 mt-2 font-medium">Latest update: 24 June 2024</p>
                    </div>
                    <div>
                        <a 
                            href="/" 
                            className="inline-flex items-center justify-center bg-white text-[#0f172a] px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-100 active:scale-95 transition-all shadow-md shadow-black/5"
                        >
                            Go to Home
                        </a>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16">
                <div className="prose prose-slate max-w-none text-gray-700 leading-relaxed text-[16px] space-y-6">
                    <p className="text-[16px] leading-[1.8] text-gray-700">
                        Welcome to gio billing. By subscribing to, accessing, or using our software, POS billing endpoints, and support portal, you agree to be bound by these Terms of Service. If you disagree, you must cease using our software immediately. Please read these terms carefully.
                    </p>

                    {/* Section 1 */}
                    <div className="pt-4">
                        <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-tight mb-4">
                            1. Acceptance of Terms
                        </h2>
                        <p className="text-[16px] leading-[1.8] text-gray-700">
                            By utilizing gio billing services, you acknowledge that you have read, understood, and agree to be bound by these terms. We reserve the right to modify these terms at any time. Your continued use of the platform after updates are posted indicates your acceptance of the revised Terms.
                        </p>
                    </div>

                    {/* Section 2 */}
                    <div className="pt-4">
                        <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-tight mb-4">
                            2. User Responsibilities
                        </h2>
                        <p className="text-[16px] leading-[1.8] text-gray-700">
                            You agree to maintain absolute confidentiality of your portal credentials. You are responsible for ensuring that all tax declarations, invoices, and payment summaries comply fully with regional tax authorities and GST frameworks. You agree to cooperate in resolving any security breaches or unauthorized accounts reported under your username.
                        </p>
                    </div>

                    {/* Section 3 */}
                    <div className="pt-4">
                        <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-tight mb-4">
                            3. Subscription & Payments
                        </h2>
                        <p className="text-[16px] leading-[1.8] text-gray-700">
                            Access to gio billing functions requires purchasing active subscription plans. All invoices are billed in advance, are recurring, and are non-refundable. Subscription details, renewals, and current rates are available on our pricing matrix. Failure to pay active invoices within the specified period may result in immediate suspension of account privileges.
                        </p>
                    </div>

                    {/* Section 4 */}
                    <div className="pt-4">
                        <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-tight mb-4">
                            4. Acceptable Use
                        </h2>
                        <p className="text-[16px] leading-[1.8] text-gray-700">
                            You agree not to bypass software authentication systems, run automated scraping bots against invoice records, host reverse-engineered versions of our codebase, or run concurrent stress tests without our explicit authorization. Any breach of system integrity will result in direct account termination.
                        </p>
                    </div>

                    {/* Section 5 */}
                    <div className="pt-4">
                        <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-tight mb-4">
                            5. Intellectual Property
                        </h2>
                        <p className="text-[16px] leading-[1.8] text-gray-700">
                            All source code, database structures, interface mockups, SVG assets, and design systems are the exclusive intellectual property of gio billing. Your subscription grants you a revocable, non-transferable, and non-exclusive license to use the system during your billing cycle.
                        </p>
                    </div>

                    {/* Section 6 */}
                    <div className="pt-4">
                        <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-tight mb-4">
                            6. Service Termination
                        </h2>
                        <p className="text-[16px] leading-[1.8] text-gray-700">
                            We reserve the right to immediately suspend your account, block database write access, or terminate service agreements if you violate any of these terms, or fail to settle outstanding payment invoices within 15 calendar days.
                        </p>
                    </div>

                    {/* Section 7 */}
                    <div className="pt-4">
                        <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-tight mb-4">
                            7. Limitation of Liability
                        </h2>
                        <p className="text-[16px] leading-[1.8] text-gray-700">
                            gio billing is provided on an "as is" and "as available" basis. To the maximum extent permitted by law, we are not liable for any lost transaction data, server downtime, financial losses, or business disruptions arising from service interruptions.
                        </p>
                    </div>

                    {/* Section 8 */}
                    <div className="pt-4">
                        <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-tight mb-4">
                            8. Governing Law
                        </h2>
                        <p className="text-[16px] leading-[1.8] text-gray-700">
                            These Terms of Service shall be governed by and construed in accordance with the laws of India, without reference to conflict of laws principles. Any legal actions or disputes shall be resolved exclusively in the courts of Chennai, India.
                        </p>
                    </div>

                    {/* Section 9 */}
                    <div className="pt-4">
                        <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-tight mb-4">
                            9. Contact Information
                        </h2>
                        <p className="text-[16px] leading-[1.8] text-gray-700">
                            For any questions, legal inquiries, or compliance notifications regarding these Terms, please contact our support desk at <a href="mailto:support@giobilling.com" className="text-gray-900 font-semibold underline hover:text-[#047857] transition-colors">support@giobilling.com</a>.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TermsPage;
